import React, { FC, useEffect, useMemo, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { UserSummaryStore } from 'src/stores/users-store';
import { validateEmail } from 'src/core/utils/object';

const UserSelect: FC<
  {
    onChange?: (value: { value: string; label: string; email?: string } | undefined, organizations?: string[]) => void;
    exclude?: string[];
    value?: { value: string; label: string };
    placeholder?: string;
    allowInviteUser?: boolean;
    disabled?: boolean;
    allowEmptyPropValue?: boolean;
  } & WithTranslation
> = ({ t, onChange, exclude, value, placeholder, allowInviteUser = false, disabled, allowEmptyPropValue }) => {
  const userStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = userStore.state;
  const [currentValue, setValue] = useState(value);
  const [inviteUserItem, setInviteUserItem] = useState<string>('');

  const load = (search: string | undefined = undefined) => {
    const filter = (exclude || []).length === 0 ? [] : [`id ne ${exclude?.join(' and id ne ')}`];

    userStore.load({
      searchQuery: search,
      orderBy: [{ field: 'DisplayName', direction: 'Ascending', useProfile: true }],
      skip: 0,
      take: 1000000,
      filter,
    } as Query);
  };

  useEffect(() => {
    let nextValue = allowEmptyPropValue ? value : value || currentValue;
    if (nextValue && (exclude || []).includes(nextValue.value)) nextValue = undefined;
    setValue(nextValue);
    load();
    allowEmptyPropValue && onChange && onChange(value);
  }, [exclude, userStore, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    allowInviteUser && setInviteUserItem(value);
    load(value);
  }, 800);

  const onSelect = (nextValue: { label: string; value: string }, option: any) => {
    const next = option == null ? undefined : { value: option.value as string, label: option.displayName as string, email: option.email };
    const orgs = JSON.parse(option?.organizations ?? '[]') || [];
    setValue(nextValue);
    if (allowInviteUser && next == null) setInviteUserItem('');
    onChange && onChange(next as any, orgs);
  };

  return (
    <Select
      disabled={disabled}
      loading={!!usersState.isBusy.get()}
      labelInValue
      placeholder={placeholder || t(allowInviteUser ? 'SelectOrInviteUser' : 'SelectUser')}
      onChange={onSelect}
      style={{ width: '100%', height: '100%' }}
      allowClear
      showSearch
      value={currentValue}
      notFoundContent={usersState.isBusy.get() ? <Spin size="small" /> : undefined}
      filterOption={false}
      showArrow={false}
      onSearch={onSearch}
      onDropdownVisibleChange={(visible) => {
        if (!visible) load();
      }}
    >
      {allowInviteUser &&
        (inviteUserItem || '').length !== 0 &&
        validateEmail(inviteUserItem) &&
        !usersState.items.value.some((x) => x.email === inviteUserItem) && (
          <Select.Option
            key={`INVITE_${inviteUserItem}`}
            value={inviteUserItem}
            organizations={'[]'}
            displayName={inviteUserItem}
            email={inviteUserItem}
          >
            <strong>{t('Invite')}:</strong> {inviteUserItem}
          </Select.Option>
        )}
      {usersState.items.value.map((o) => (
        <Select.Option
          email={o.email}
          key={o.id}
          value={o.id}
          organizations={JSON.stringify(o.organizations.map((x) => x.organizationId))}
          displayName={o.displayName}
        >
          {o.displayName}
        </Select.Option>
      ))}
    </Select>
  );
};

export default withTranslation()(UserSelect);
